import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { itemsAPI, batchesAPI } from '../services/api';
import { getFromCache } from '../services/cacheService';
import api from '../services/api';
import { createSale } from '../api/sales';
import debtorsService from '../api/debtors';
import { useAuth } from '../context/AuthContext';
import { getItemName } from '../utils/itemHelpers';
import Navbar from '../components/Navbar';
import '../styles/POS.css';

const POS = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [popularItems, setPopularItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [notes, setNotes] = useState('');
  
  // Customer states
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  
  // Measurement input states (for weight, length, area, volume)
  const [showMeasurementModal, setShowMeasurementModal] = useState(false);
  const [measurementInputItem, setMeasurementInputItem] = useState(null);
  const [measurementInput, setMeasurementInput] = useState('');

  const fetchItems = useCallback(async () => {
    try {
      setLoadingItems(true);
      
      try {
        const response = await itemsAPI.getItems({ page_size: 1000 });
        // Handle both direct array and paginated responses
        const itemsData = Array.isArray(response.data) 
          ? response.data 
          : response.data.results || response.data;
        
        const normalized = itemsData.map((item) => ({
          ...item,
          selling_price: item.latest_batch_unit_selling_price ?? item.selling_price,
          currency: item.latest_batch_currency ?? item.currency,
          available_quantity: typeof item.total_quantity === 'number'
            ? item.total_quantity
            : (item.quantity || 0)
        }));

        // Filter only valid available items
        const filtered = normalized.filter(item => 
          item.available_quantity > 0 
          && item.is_active === true
          && item.name
          && item.name.trim() !== ''
        );
        setItems(filtered);
      } catch (error) {
        // If network request fails, try to use cached data
        if (!navigator.onLine) {
          console.log('[POS] Fetching from cache due to offline mode');
          const cachedItems = await getFromCache('ITEMS');
          
          if (cachedItems) {
            const normalized = (Array.isArray(cachedItems) ? cachedItems : cachedItems.results || cachedItems).map((item) => ({
              ...item,
              selling_price: item.latest_batch_unit_selling_price ?? item.selling_price,
              currency: item.latest_batch_currency ?? item.currency,
              available_quantity: typeof item.total_quantity === 'number'
                ? item.total_quantity
                : (item.quantity || 0)
            }));
            
            const filtered = normalized.filter(item => 
              item.available_quantity > 0 
              && item.is_active === true
              && item.name
              && item.name.trim() !== ''
            );
            setItems(filtered);
            return;
          }
        }
        
        console.error('Error fetching items:', error);
        toast.error(t('errorFetchingItems'));
      }
    } finally {
      setLoadingItems(false);
    }
  }, [t]);

  const fetchPopularItems = useCallback(async () => {
    try {
      const response = await api.get('/sales/popular_items/?limit=8');
      if (response.data && response.data.popular_items) {
        console.log('[POS] Popular items received:', response.data.popular_items);
        setPopularItems(response.data.popular_items);
      }
    } catch (error) {
      console.warn('Popular items not available:', error.message);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await debtorsService.getDebtors();
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchPopularItems();
    fetchCustomers();
  }, [fetchItems, fetchPopularItems, fetchCustomers]);

  const addToCart = (item, measurement = null) => {
    // Safety guard: never sell inactive or out-of-stock items.
    if (!item?.is_active || Number(item.available_quantity || 0) <= 0) {
      toast.error(t('insufficientStock'));
      return;
    }

    // Validate item has required data
    const price = parseFloat(item.selling_price);
    if (!price || price <= 0 || isNaN(price)) {
      toast.error(`${t('invalidItem')}: ${getItemName(item, i18n.language)} - ${t('noPriceSet')}`);
      console.error('Item missing valid selling_price:', item);
      return;
    }
    
    // Check if item uses non-quantity unit type and measurement is not provided
    const requiresMeasurement = item.unit_type && item.unit_type !== 'quantity';
    if (requiresMeasurement && measurement === null) {
      // Open measurement input modal
      setMeasurementInputItem(item);
      setMeasurementInput('');
      setShowMeasurementModal(true);
      return;
    }
    
    // Ensure selling_price is a number
    const validatedItem = {
      ...item,
      selling_price: price
    };
    
    const cartKey = validatedItem.batch_id ? `${validatedItem.id}-${validatedItem.batch_id}` : `${validatedItem.id}`;
    const existingItem = cart.find(i => i.cartKey === cartKey);
    
    if (existingItem) {
      // For measurement-based items (weight, length, area, volume)
      if (requiresMeasurement) {
        const newMeasurement = parseFloat(existingItem.cartMeasurement || 0) + parseFloat(measurement);
        if (newMeasurement > validatedItem.available_quantity) {
          toast.error(`${t('insufficientStock')}: ${getItemName(validatedItem, i18n.language)}`);
          return;
        }
        setCart(cart.map(i =>
          i.cartKey === cartKey
            ? { ...i, cartMeasurement: newMeasurement }
            : i
        ));
      } else {
        // For quantity-based items (allow fractional if enabled)
        const increment = validatedItem.allow_fractional ? 1 : 1;
        const newQuantity = existingItem.cartQuantity + increment;
        if (newQuantity > validatedItem.available_quantity) {
          toast.error(`${t('insufficientStock')}: ${getItemName(validatedItem, i18n.language)}`);
          return;
        }
        setCart(cart.map(i =>
          i.cartKey === cartKey
            ? { ...i, cartQuantity: newQuantity }
            : i
        ));
      }
    } else {
      // New item to cart
      if (requiresMeasurement) {
        setCart([...cart, { ...validatedItem, cartMeasurement: parseFloat(measurement), cartQuantity: 0, cartKey }]);
      } else {
        setCart([...cart, { ...validatedItem, cartQuantity: 1, cartMeasurement: 0, cartKey }]);
      }
    }
    
    toast.success(`${getItemName(item, i18n.language)} ${t('addedToCart')}`, { autoClose: 1000 });
  };
  
  const handleConfirmMeasurement = () => {
    const measurement = parseFloat(measurementInput);
    if (!measurement || measurement <= 0 || isNaN(measurement)) {
      toast.error(t('enterValidMeasurement') || 'Please enter a valid measurement');
      return;
    }
    
    if (measurement > measurementInputItem.available_quantity) {
      toast.error(`${t('insufficientStock')}: ${getItemName(measurementInputItem, i18n.language)}`);
      return;
    }
    
    addToCart(measurementInputItem, measurement);
    setShowMeasurementModal(false);
    setMeasurementInputItem(null);
    setMeasurementInput('');
  };

  const addWholeBoxToCart = async (item) => {
    try {
      // Fetch batch details to check if box is full
      const batchId = item.latest_batch_id;
      if (!batchId) {
        toast.error(t('noBatchAvailable') || 'No batch available for this item');
        return;
      }

      const batchResponse = await batchesAPI.getBatch(batchId);
      const batch = batchResponse.data;

      // Check if at least one full box is still available in this batch.
      const oneBoxQuantity = parseFloat(batch.items_per_box) || 1;
      const batchRemainingQuantity = parseFloat(batch.quantity) || 0;
      if (oneBoxQuantity > batchRemainingQuantity) {
        toast.error(t('insufficientStockForBox') || 'Insufficient stock for a whole box');
        return;
      }

      // Use box selling price
      const boxPrice = parseFloat(batch.box_selling_price);
      if (!boxPrice || boxPrice <= 0 || isNaN(boxPrice)) {
        toast.error(t('noBoxPriceSet') || 'Box price not set for this batch');
        return;
      }

      const cartKey = `${item.id}-${batchId}-box`;
      const existingItem = cart.find(i => i.cartKey === cartKey);

      if (existingItem) {
        // Add another box (each box = 1 × items_per_box)
        const newBoxCount = existingItem.boxCount + 1;
        const totalQuantity = newBoxCount * oneBoxQuantity;
        if (totalQuantity > batchRemainingQuantity) {
          toast.error(`${t('insufficientStock')}: ${getItemName(item, i18n.language)}`);
          return;
        }
        setCart(cart.map(i =>
          i.cartKey === cartKey
            ? { ...i, boxCount: newBoxCount, cartQuantity: totalQuantity }
            : i
        ));
      } else {
        // Add new box to cart (1 box = items_per_box)
        const boxItem = {
          ...item,
          selling_price: boxPrice,
          cartQuantity: oneBoxQuantity,
          boxCount: 1,
          isWholeBox: true,
          batch_id: batchId,
          latest_batch_items_per_box: oneBoxQuantity,
          latest_batch_number_of_boxes: batch.number_of_boxes,
          batch_available_quantity: batchRemainingQuantity,
          cartKey,
          cartMeasurement: 0
        };
        setCart([...cart, boxItem]);
      }

      toast.success(getWholeBoxAddedMessage(item), { autoClose: 1500 });
    } catch (error) {
      console.error('Error adding whole box:', error);
      toast.error(t('errorAddingBox') || 'Error adding whole box to cart');
    }
  };

  const handleSearchKeyPress = async (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      const barcode = searchTerm.trim();
      try {
        const batchResponse = await batchesAPI.lookupByBarcode(barcode);
        const batch = batchResponse.data;

        const resolvedUnitPrice =
          parseFloat(batch.unit_selling_price) ||
          (parseFloat(batch.box_selling_price) && parseFloat(batch.items_per_box)
            ? parseFloat(batch.box_selling_price) / parseFloat(batch.items_per_box)
            : 0);

        const batchItem = {
          id: batch.item,
          name: batch.item_name,
          name_ar: batch.item_name_ar,
          name_fr: batch.item_name_fr,
          currency: batch.currency,
          selling_price: resolvedUnitPrice,
          available_quantity: batch.quantity,
          batch_id: batch.id,
          batch_code: batch.batch_code
        };

        addToCart(batchItem);
        setSearchTerm('');
        return;
      } catch (error) {
        const item = items.find(i => i.barcode === barcode);
        if (item) {
          addToCart(item);
          setSearchTerm('');
        } else {
          toast.error(t('itemNotFound'));
        }
      }
    }
  };

  const removeFromCart = (cartKey) => {
    setCart(cart.filter(i => i.cartKey !== cartKey));
  };

  const updateQuantity = (cartKey, quantity, isMeasurement = false) => {
    const item = cart.find(i => i.cartKey === cartKey);
    
    if (quantity > item.available_quantity) {
      toast.error(`${t('insufficientStock')}: ${getItemName(item, i18n.language)}`);
      return;
    }
    
    if (quantity <= 0) {
      removeFromCart(cartKey);
      return;
    }
    
    setCart(cart.map(i =>
      i.cartKey === cartKey
        ? isMeasurement 
          ? { ...i, cartMeasurement: quantity }
          : { ...i, cartQuantity: quantity }
        : i
    ));
  };

  const parseValidExchangeRate = (rate) => {
    const parsed = Number(rate);
    return Number.isFinite(parsed) && parsed > 1 ? parsed : null;
  };

  const resolveExchangeRate = (itemOrSale) => {
    const itemRate = parseValidExchangeRate(itemOrSale?.exchange_rate);
    const firstItemRate = parseValidExchangeRate(itemOrSale?.items?.[0]?.exchange_rate);
    const userRate = parseValidExchangeRate(user?.exchange_rate);
    return itemRate || firstItemRate || userRate || 89500;
  };

  const calculateTotals = () => {
    let totalUSD = 0;
    let totalLBP = 0;

    cart.forEach(item => {
      const exchangeRate = resolveExchangeRate(item);
      // Whole-box line uses boxCount; measurement items use measured value; normal items use quantity.
      const requiresMeasurement = item.unit_type && item.unit_type !== 'quantity';
      const multiplier = item.isWholeBox
        ? (item.boxCount || 0)
        : (requiresMeasurement ? (item.cartMeasurement || 0) : (item.cartQuantity || 0));
      const sellingPrice = parseFloat(item.selling_price);

      if (item.currency === 'USD') {
        totalUSD += sellingPrice * multiplier;
        totalLBP += sellingPrice * multiplier * exchangeRate;
      } else {
        totalLBP += sellingPrice * multiplier;
        totalUSD += (sellingPrice * multiplier) / exchangeRate;
      }
    });

    return { totalUSD, totalLBP };
  };

  const getWholeBoxLabel = () => {
    const label = t('sellWholeBox');
    if (label === 'sellWholeBox' || label === 'sellwollbox' || label === 'sellwellbox') {
      return i18n.language === 'ar' ? 'بيع صندوق كامل' : 'Sell Whole Box';
    }
    return label;
  };

  const getWholeBoxAddedMessage = (item) => {
    const itemName = getItemName(item, i18n.language);
    const translated = t('wholeBoxAdded', { item: itemName });

    if (!translated || translated === 'wholeBoxAdded') {
      if (i18n.language === 'ar') return `تمت إضافة صندوق: ${itemName}`;
      if (i18n.language === 'fr') return `Carton ajoute: ${itemName}`;
      return `Whole box added: ${itemName}`;
    }

    return translated;
  };

  const handleAddCustomer = async () => {
    if (!newCustomerName.trim() || !newCustomerPhone.trim()) {
      toast.error(t('namePhoneRequired') || 'Name and phone are required');
      return;
    }

    try {
      const response = await debtorsService.createDebtor({
        name: newCustomerName,
        phone: newCustomerPhone,
      });
      
      setCustomers([...customers, response.data]);
      setSelectedCustomer(response.data);
      setShowCustomerModal(false);
      setNewCustomerName('');
      setNewCustomerPhone('');
      toast.success(t('debtAdded') || 'Customer added successfully');
    } catch (error) {
      console.error('Error adding customer:', error);
      toast.error(t('failedToSaveDebtor') || 'Failed to add customer');
    }
  };

  const handleCheckout = async () => {
    if (isCheckingOut) {
      return;
    }

    if (cart.length === 0) {
      toast.error(t('cartEmpty'));
      return;
    }

    // Validate debt payment requires customer
    if (paymentMethod === 'debt' && !selectedCustomer) {
      toast.error(t('selectCustomerForDebt') || 'Please select a customer for debt payment');
      return;
    }

    setIsCheckingOut(true);

    try {
      const saleData = {
        client_sale_date: new Date().toISOString(),
        items: cart.map(item => {
          const requiresMeasurement = item.unit_type && item.unit_type !== 'quantity';
          const actualQuantity = requiresMeasurement ? (item.cartMeasurement || 1) : (item.cartQuantity || 1);
          const itemData = {
            item_id: item.id,
            batch_id: item.batch_id || null,
            quantity: actualQuantity
          };
          
          // Add flag for whole box sales
          if (item.isWholeBox) {
            itemData.is_box_sale = true;
            itemData.box_price = parseFloat(item.selling_price);
          }
          
          // Add measurement_value if item uses measurement-based units (weight, length, area, volume)
          if (requiresMeasurement && item.cartMeasurement) {
            itemData.measurement_value = item.cartMeasurement;
            // Keep weight_kg for backward compatibility
            if (item.unit_type === 'weight') {
              itemData.weight_kg = item.cartMeasurement;
            }
          }
          
          return itemData;
        }),
        payment_method: paymentMethod,
        notes: notes,
        customer_id: selectedCustomer ? selectedCustomer.id : null
      };

      const sale = await createSale(saleData, cart);
      if (!sale.customer_name && selectedCustomer) {
        sale.customer_name = selectedCustomer.name;
        sale.customer_phone = selectedCustomer.phone;
        sale.debtor_name = selectedCustomer.name;
        sale.debtor_phone = selectedCustomer.phone;
      }
      // Ensure is_debt is set for debt sales
      if (paymentMethod === 'debt' && !sale.is_debt) {
        sale.is_debt = true;
      }
      setLastSale(sale);
      setShowReceipt(true);
      setCart([]);
      setNotes('');
      setSelectedCustomer(null);
      fetchItems(); // Refresh items
      toast.success(t('saleCompleted'));
    } catch (error) {
      const backendError = String(error?.response?.data?.error || '').trim();
      const normalized = backendError.toLowerCase();

      // Normalize known stock errors coming from backend to translated UI message.
      if (
        normalized.includes('insufficient stock') ||
        normalized.includes('inssufisant stock') ||
        normalized.includes('item is inactive') ||
        normalized.includes('batch is inactive')
      ) {
        toast.error(t('insufficientStock'));
      } else {
        toast.error(backendError || t('errorCreatingSale'));
      }
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const filteredItems = items.filter(item =>
    getItemName(item, i18n.language).toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.barcode?.includes(searchTerm)
  );

  const itemsById = items.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});

  // Merge popular items with full item data
  const stockedPopularItems = popularItems
    .map((popularItem) => {
      const fullItem = itemsById[popularItem.id];
      if (!fullItem) {
        console.log('[POS] Skipping popular item not in active items list:', popularItem.id, popularItem.name);
        return null;
      }
      
      // Merge popular item stats with full item data
      return {
        ...fullItem,
        total_sold: popularItem.total_sold,
      };
    })
    .filter(item => {
      if (!item) return false;
      if (item.is_active !== true) return false;
      if (Number(item.available_quantity || 0) <= 0) return false;
      const hasPrice = item.selling_price && parseFloat(item.selling_price) > 0;
      if (!hasPrice) {
        console.warn('[POS] Popular item missing price:', item.name, item);
      }
      return hasPrice;
    });
  
  console.log('[POS] Stocked popular items:', stockedPopularItems.length, stockedPopularItems);

  const { totalUSD, totalLBP } = calculateTotals();

  return (
    <div>
      <Navbar />
      <div className="pos-container" dir={i18n.dir()}>
        {!showReceipt ? (
          <>
            {/* Header */}
            <div className="pos-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1>{t('pointOfSale')}</h1>
            <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
              {loadingItems && <span>{t('loading')}...</span>}
            </div>
          </div>

          <div className="pos-content">
            {/* Left side - Items */}
            <div className="pos-items-section">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                <h2 style={{ margin: 0 }}>{t('items')}</h2>
                <button
                  onClick={() => {
                    setLoadingItems(true);
                    fetchItems();
                  }}
                  disabled={loadingItems}
                  className="refresh-btn"
                  title={t('refresh')}
                >
                  🔄 {t('refresh')}
                </button>
              </div>
              
              <input
                type="text"
                className="search-input"
                placeholder={t('searchItemsOrBarcode')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                autoFocus
              />

            {/* Popular Items Section */}
            {stockedPopularItems.length > 0 && (
              <div className="popular-items-section">
                <h3>{t('popularItems') || 'Popular Items'}</h3>
                <div className="popular-items-grid">
                  {stockedPopularItems.map(item => {
                    const price = parseFloat(item.selling_price || 0);
                    return (
                      <button
                        key={item.id}
                        className="popular-item-btn"
                        onClick={() => addToCart(item)}
                        title={`${getItemName(item, i18n.language)} (${t('sold')}: ${item.total_sold})`}
                      >
                        <div className="popular-item-name">{getItemName(item, i18n.language)}</div>
                        <div className="popular-item-price">
                          {item.currency === 'USD' 
                            ? `$${price.toLocaleString('en-US')}`
                            : `${price.toLocaleString('en-US')} ${t('lbpSymbol')}`
                          }
                        </div>
                        <div className="popular-item-sold">{t('sold')}: {item.total_sold}</div>
                        {item.latest_batch_code && (
                          <div className="popular-item-batch">
                            <span style={{fontSize: '0.75em', color: '#666'}}>
                              {item.latest_batch_code}
                              {item.latest_batch_expiry_date && ` • ${t('exp')}: ${new Date(item.latest_batch_expiry_date).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {month: 'short', year: '2-digit'})}`}
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <h3>{t('allItems') || 'All Items'}</h3>
            <div className="items-grid">
              {filteredItems && filteredItems.length > 0 ? (
                filteredItems.map(item => (
                  <div key={item.id} className="item-card">
                    <div onClick={() => addToCart(item)} style={{cursor: 'pointer'}}>
                      <div className="item-name">{getItemName(item, i18n.language)}</div>
                      <div className="item-price">
                        {item.currency === 'USD' 
                          ? `$${parseFloat(item.selling_price || 0).toLocaleString('en-US')}`
                          : `${parseFloat(item.selling_price || 0).toLocaleString('en-US')} ${t('lbpSymbol')}`
                        }
                      </div>
                      <div className="item-stock">{t('stock')}: {item.available_quantity}</div>
                      {item.latest_batch_code && (
                        <div className="item-batch-info">
                          <span className="batch-code">{item.latest_batch_code}</span>
                          {item.latest_batch_expiry_date && (
                            <span className="batch-expiry">
                              {t('exp')}: {new Date(item.latest_batch_expiry_date).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {item.latest_batch_id && item.latest_batch_box_selling_price && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          addWholeBoxToCart(item);
                        }}
                        className="whole-box-btn"
                        style={{
                          marginTop: '8px',
                          padding: '6px 10px',
                          background: '#2980b9',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.85em',
                          width: '100%'
                        }}
                      >
                        📦 {getWholeBoxLabel()} - {item.currency === 'USD' 
                          ? `$${parseFloat(item.latest_batch_box_selling_price).toLocaleString('en-US')}`
                          : `${parseFloat(item.latest_batch_box_selling_price).toLocaleString('en-US')} ${t('lbpSymbol')}`
                        }
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <div className="no-items">{t('noItems') || 'No items available'}</div>
              )}
            </div>
          </div>

          {/* Right side - Cart */}
          <div className="pos-cart-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
              <h2 style={{ margin: 0 }}>{t('cart')}</h2>
              <span className="cart-badge" style={{ 
                background: '#e74c3c', 
                color: 'white', 
                borderRadius: '50%', 
                width: '30px', 
                height: '30px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                {cart.length}
              </span>
            </div>
            
            {cart.length === 0 ? (
              <div className="empty-cart">{t('cartEmpty')}</div>
            ) : (
              <>
                <div className="cart-items">
                  {cart.map(item => (
                    <div key={item.cartKey} className="cart-item">
                      <div className="cart-item-info">
                        <div className="cart-item-name">
                          {getItemName(item, i18n.language)}
                          {item.isWholeBox && (
                            <span style={{
                              marginLeft: '8px',
                              background: '#2980b9',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '0.75em',
                              fontWeight: 'bold'
                            }}>
                              📦 {getWholeBoxLabel()}
                            </span>
                          )}
                        </div>
                        <div className="cart-item-price">
                          {item.currency === 'USD'
                            ? `$${parseFloat(item.selling_price || 0).toLocaleString('en-US')}`
                            : `${parseFloat(item.selling_price || 0).toLocaleString('en-US')} ${t('lbpSymbol')}`
                          }
                          {item.isWholeBox && item.boxCount && (
                            <span style={{ marginLeft: '6px', fontSize: '0.85em', color: '#666' }}>
                              × {item.boxCount} {t('boxes') || 'box(es)'}
                            </span>
                          )}
                        </div>
                        {item.latest_batch_code && (
                          <div className="cart-item-batch">
                            <span className="batch-code-small">{item.latest_batch_code}</span>
                            {item.latest_batch_expiry_date && (
                              <span className="batch-expiry-small">
                                {t('exp')}: {new Date(item.latest_batch_expiry_date).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {month: 'short', year: 'numeric'})}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="cart-item-controls">
                        {item.isWholeBox ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <button
                                onClick={() => {
                                  const newBoxCount = item.boxCount - 1;
                                  if (newBoxCount === 0) {
                                    removeFromCart(item.cartKey);
                                  } else {
                                    const itemsPerBox = item.latest_batch_items_per_box || 1;
                                    const newQuantity = newBoxCount * itemsPerBox;
                                    setCart(cart.map(i =>
                                      i.cartKey === item.cartKey
                                        ? { ...i, boxCount: newBoxCount, cartQuantity: newQuantity }
                                        : i
                                    ));
                                  }
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#e74c3c',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '1em'
                                }}
                              >
                                −
                              </button>
                              <span style={{ fontSize: '1em', fontWeight: 'bold', minWidth: '30px', textAlign: 'center' }}>
                                {item.boxCount} 📦
                              </span>
                              <button
                                onClick={() => {
                                  const itemsPerBox = item.latest_batch_items_per_box || 1;
                                  const newBoxCount = item.boxCount + 1;
                                  const newQuantity = newBoxCount * itemsPerBox;
                                  const maxBatchQuantity = item.batch_available_quantity || item.available_quantity;
                                  if (newQuantity > maxBatchQuantity) {
                                    toast.error(`${t('insufficientStock')}: ${getItemName(item, i18n.language)}`);
                                    return;
                                  }
                                  setCart(cart.map(i =>
                                    i.cartKey === item.cartKey
                                      ? { ...i, boxCount: newBoxCount, cartQuantity: newQuantity }
                                      : i
                                  ));
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#27ae60',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '1em'
                                }}
                              >
                                +
                              </button>
                            </div>
                            <div style={{ marginTop: '4px', fontSize: '0.8em', color: '#666' }}>
                              {item.cartQuantity} {t('items') || 'items'}
                            </div>
                          </>
                        ) : item.unit_type && item.unit_type !== 'quantity' ? (
                          <>
                            <label style={{ fontSize: '0.85em', marginRight: '5px', color: '#666' }}>
                              {item.unit_type === 'weight' && (t('weightKg') || 'Weight (kg)')}
                              {item.unit_type === 'length' && (t('lengthM') || 'Length (m)')}
                              {item.unit_type === 'area' && (t('areaM2') || 'Area (m²)')}
                              {item.unit_type === 'volume' && (t('volumeL') || 'Volume (L)')}
                              :
                            </label>
                            <input
                              type="number"
                              step="0.001"
                              min="0.001"
                              max={item.available_quantity}
                              value={item.cartMeasurement || 0}
                              onChange={(e) => updateQuantity(item.cartKey, parseFloat(e.target.value) || 0, true)}
                              className="quantity-input"
                              style={{ width: '80px' }}
                            />
                          </>
                        ) : (
                          <>
                            <input
                              type="number"
                              step={item.allow_fractional ? "0.01" : "1"}
                              min={item.allow_fractional ? "0.01" : "1"}
                              max={item.available_quantity}
                              value={item.cartQuantity}
                              onChange={(e) => {
                                const value = item.allow_fractional 
                                  ? parseFloat(e.target.value) || 0
                                  : parseInt(e.target.value) || 0;
                                updateQuantity(item.cartKey, value);
                              }}
                              className="quantity-input"
                            />
                          </>
                        )}
                        <button
                          onClick={() => removeFromCart(item.cartKey)}
                          className="remove-btn"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="cart-totals">
                  <div className="total-row">
                    <span>{t('totalUSD')}:</span>
                    <span className="total-amount">${totalUSD.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </div>
                  <div className="total-row">
                    <span>{t('totalLBP')}:</span>
                    <span className="total-amount">{totalLBP.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} {t('lbpSymbol')}</span>
                  </div>
                </div>

                <div className="payment-method">
                  <label>{t('paymentMethod')}:</label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="payment-select"
                  >
                    <option value="cash">{t('cash')}</option>
                    <option value="card">{t('card')}</option>
                    <option value="both">{t('both')}</option>
                    <option value="debt">{t('debt')}</option>
                  </select>
                </div>

                {/* Customer Selection */}
                <div className="customer-section" style={{ marginTop: '15px', marginBottom: '15px' }}>
                  <label>{t('customer') || 'Customer'}: {paymentMethod === 'debt' && <span style={{color: 'red'}}>*</span>}</label>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                    <select
                      value={selectedCustomer ? selectedCustomer.id : ''}
                      onChange={(e) => {
                        const customer = customers.find(c => c.id === parseInt(e.target.value));
                        setSelectedCustomer(customer || null);
                      }}
                      className="payment-select"
                      style={{ flex: 1 }}
                    >
                      <option value="">{t('selectCustomer') || 'Select Customer'}</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name} - {customer.phone}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowCustomerModal(true)}
                      className="add-customer-btn"
                      style={{
                        padding: '8px 15px',
                        background: '#27ae60',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      + {t('addCustomer') || 'Add'}
                    </button>
                  </div>
                  {selectedCustomer && (
                    <div style={{ 
                      marginTop: '10px', 
                      padding: '10px', 
                      background: '#f0f0f0', 
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}>
                      <div><strong>{t('name')}:</strong> {selectedCustomer.name}</div>
                      <div><strong>{t('phone')}:</strong> {selectedCustomer.phone}</div>
                      {selectedCustomer.remaining_debt > 0 && (
                        <div style={{ color: '#e74c3c', marginTop: '5px' }}>
                          <strong>{t('remainingDebt')}:</strong> ${parseFloat(selectedCustomer.remaining_debt).toFixed(2)}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>{t('notes')}:</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="notes-textarea"
                    placeholder={t('enterNotes') || 'Enter notes (optional)'}
                    rows="2"
                  />
                </div>

                <button onClick={handleCheckout} className="checkout-btn" disabled={isCheckingOut}>
                  {isCheckingOut ? `${t('loading')}...` : t('checkout')}
                </button>
              </>
            )}
          </div>
          </div>
        </>
      ) : (
        <div className="receipt-container">
          <div className="receipt-header-section">
          </div>
          
          <div className="receipt" id="receipt">
            <div className="receipt-header">
              <h2>{user.market_name}</h2>
              <p>{t('receipt')}</p>
              <p>{new Date(lastSale.sale_date).toLocaleString(i18n.language)}</p>
              
              {/* Customer Info */}
              {(lastSale.customer_name || lastSale.debtor_name) && (
                <div style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                  <strong>{t('customer') || 'Customer'}:</strong>
                  <div style={{ marginTop: '5px' }}>
                    <div>{lastSale.customer_name || lastSale.debtor_name}</div>
                    <div>{lastSale.customer_phone || lastSale.debtor_phone}</div>
                  </div>
                </div>
              )}
              {lastSale.payment_method && (
                <div dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} style={{ marginTop: '10px', textAlign: i18n.language === 'ar' ? 'right' : 'left' }}>
                  <strong>{t('paymentMethod')}:</strong>{' '}
                  {lastSale.payment_method === 'cash' && t('cash')}
                  {lastSale.payment_method === 'card' && t('card')}
                  {lastSale.payment_method === 'both' && t('both')}
                  {lastSale.payment_method === 'debt' && (
                    <span style={{ color: '#e74c3c' }}>{t('debt')}</span>
                  )}
                </div>
              )}
            </div>

            <div className="receipt-items">
              <table>
                <thead>
                  <tr>
                    <th>{t('item')}</th>
                    <th>{t('qty')}</th>
                    <th>{t('price')}</th>
                    <th>{t('total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {lastSale.items.map(item => {
                    const qty = parseFloat(item.quantity) || 0;
                    const qtyDisplay = qty % 1 === 0 ? qty.toFixed(0) : qty.toFixed(3).replace(/\.?0+$/, '');
                    const localizedItemName = i18n.language === 'ar'
                      ? (item.item_name_ar || item.item_name)
                      : i18n.language === 'fr'
                        ? (item.item_name_fr || item.item_name)
                        : item.item_name;
                    return (
                    <tr key={item.id}>
                      <td>{localizedItemName}</td>
                      <td>{qtyDisplay}</td>
                      <td>
                        {item.price_usd > 0 && (
                          <div>${item.price_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                        )}
                        {item.price_lbp > 0 && (
                          <div>{item.price_lbp.toLocaleString('en-US', { minimumFractionDigits: 2 })} {t('lbpSymbol')}</div>
                        )}
                      </td>
                      <td>
                        {item.subtotal_usd > 0 && (
                          <div>${item.subtotal_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                        )}
                        {item.subtotal_lbp > 0 && (
                          <div>{item.subtotal_lbp.toLocaleString('en-US', { minimumFractionDigits: 2 })} {t('lbpSymbol')}</div>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="receipt-totals">
              {(() => {
                const totalUSD = parseFloat(lastSale.total_usd || 0);
                const totalLBP = parseFloat(lastSale.total_lbp || 0);
                const exchangeRate = resolveExchangeRate(lastSale);
                const grandTotalUSD = totalUSD + (totalLBP / exchangeRate);
                const grandTotalLBP = (totalUSD * exchangeRate) + totalLBP;

                return (
                  <>
                    {totalUSD > 0 && (
                      <div className="receipt-total-row">
                        <strong>{t('totalUSD')}:</strong>
                        <strong>${totalUSD.toLocaleString('en-US', {minimumFractionDigits: 2})}</strong>
                      </div>
                    )}
                    {totalLBP > 0 && (
                      <div className="receipt-total-row">
                        <strong>{t('totalLBP')}:</strong>
                        <strong>{totalLBP.toLocaleString('en-US', {minimumFractionDigits: 2})} {t('lbpSymbol')}</strong>
                      </div>
                    )}
                    {(totalUSD > 0 || totalLBP > 0) && (
                      <>
                        <div className="divider"></div>
                        <div className="receipt-total-row grand">
                          <strong>{t('grandTotal')}:</strong>
                          <strong>${grandTotalUSD.toLocaleString('en-US', {minimumFractionDigits: 2})}</strong>
                        </div>
                        <div className="receipt-total-row grand">
                          <strong>{t('grandTotal')}:</strong>
                          <strong>{grandTotalLBP.toLocaleString('en-US', {minimumFractionDigits: 2})} {t('lbpSymbol')}</strong>
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="receipt-footer">
              <p>{t('thankYou')}</p>
            </div>
          </div>

          <div className="receipt-actions">
            <button onClick={handlePrint} className="print-btn">
              {t('print')}
            </button>
            <button onClick={() => setShowReceipt(false)} className="new-sale-btn">
              {t('newSale')}
            </button>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showCustomerModal && (
        <div className="modal-overlay" onClick={() => setShowCustomerModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('addNewDebtor') || 'Add New Customer'}</h3>
            <div className="form-group">
              <label>{t('debtorName')}:</label>
              <input
                type="text"
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                placeholder={t('debtorName') || 'Customer Name'}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>{t('debtorPhone')}:</label>
              <input
                type="text"
                value={newCustomerPhone}
                onChange={(e) => setNewCustomerPhone(e.target.value)}
                placeholder={t('debtorPhone') || 'Customer Phone'}
                className="form-input"
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={handleAddCustomer}
                className="modal-btn primary"
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {t('save') || 'Save'}
              </button>
              <button
                onClick={() => {
                  setShowCustomerModal(false);
                  setNewCustomerName('');
                  setNewCustomerPhone('');
                }}
                className="modal-btn secondary"
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {t('cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Measurement Input Modal (for weight, length, area, volume) */}
      {showMeasurementModal && measurementInputItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '12px',
            minWidth: '400px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#2c3e50' }}>
              {measurementInputItem.unit_type === 'weight' && (t('enterWeight') || 'Enter Weight')}
              {measurementInputItem.unit_type === 'length' && (t('enterLength') || 'Enter Length')}
              {measurementInputItem.unit_type === 'area' && (t('enterArea') || 'Enter Area')}
              {measurementInputItem.unit_type === 'volume' && (t('enterVolume') || 'Enter Volume')}
            </h3>
            <p style={{ marginBottom: '15px', color: '#555' }}>
              {getItemName(measurementInputItem, i18n.language)}
            </p>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#555' }}>
                {measurementInputItem.unit_type === 'weight' && (t('weightKg') || 'Weight (kg)')}
                {measurementInputItem.unit_type === 'length' && (t('lengthM') || 'Length (m)')}
                {measurementInputItem.unit_type === 'area' && (t('areaM2') || 'Area (m²)')}
                {measurementInputItem.unit_type === 'volume' && (t('volumeL') || 'Volume (L)')}
                :
              </label>
              <input
                type="number"
                step="0.001"
                min="0.001"
                value={measurementInput}
                onChange={(e) => setMeasurementInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmMeasurement();
                  }
                }}
                placeholder="0.000"
                autoFocus
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '1.1em',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  boxSizing: 'border-box'
                }}
              />
              <small style={{ display: 'block', marginTop: '6px', color: '#888' }}>
                {t('availableQuantity') || 'Available'}: {measurementInputItem.available_quantity} 
                {measurementInputItem.unit_type === 'weight' && ' kg'}
                {measurementInputItem.unit_type === 'length' && ' m'}
                {measurementInputItem.unit_type === 'area' && ' m²'}
                {measurementInputItem.unit_type === 'volume' && ' L'}
              </small>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleConfirmMeasurement}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '1em',
                  fontWeight: '600'
                }}
              >
                {t('confirm') || 'Confirm'}
              </button>
              <button
                onClick={() => {
                  setShowMeasurementModal(false);
                  setMeasurementInputItem(null);
                  setMeasurementInput('');
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '1em'
                }}
              >
                {t('cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default POS;
